'use client';

/**
 * **시작하기 체크리스트** — 지금 워크스페이스의 저장값에서 네 줄을 센다(체크 표는 저장하지 않는다).
 *
 * ```
 *   useOnboardingChecklist()   구독본으로 센다 — 체크리스트 카드
 *   useOnboardingBadge()       auth 사본으로만 센다 — 사이드바 배지(구독을 걸지 않는다)
 * ```
 * 🔑 시작하기 칸이 없는 워크스페이스는 대상이 아니다 — 카드·배지가 서지 않는다.
 * ⚠️ 배지의 「키워드·계정 추적」은 워크스페이스 파생 칸(`keywords`·`accounts` — 세트 전체의 조회어·계정)으로 센다.
 */

import { useMemo } from 'react';

import { useUser } from 'src/hooks/use-user';

import { findCurrentWorkspace } from 'src/utils/util-user';

import { toOnboardingBadge, computeOnboardingChecklist } from 'src/sections/onboarding/utils/onboarding-state';

import { useOnboardingSources } from '../load/use-onboarding-sources';

export function useOnboardingChecklist() {
  const user = useUser();
  const workspaceId = String(user?.lastWorkspace ?? '').trim();
  const sources = useOnboardingSources(workspaceId || null);

  const list = useMemo(
    () => computeOnboardingChecklist({
      brandKeywords: sources.brandKeywords,
      campaigns: sources.campaigns,
    }),
    [sources.brandKeywords, sources.campaigns],
  );

  return {
    list,
    status: sources.status,
    /** 시작하기 대상이고 아직 다 안 했다 */
    show: toOnboardingBadge(list, sources.status) !== null,
  };
}

/**
 * 사이드바 「추적 관리」 옆 배지 — 「1/2」. 다 했거나 대상이 아니면 `null`.
 * ⚠️ 구독하지 않는다 — 레이아웃에 늘 붙는 자리라 auth 사본(라우트가 바뀔 때 갱신)으로만 센다.
 */
export function useOnboardingBadge(): string | null {
  const user = useUser();
  const workspace = findCurrentWorkspace(user) as Record<string, any> | null;
  return useMemo(() => {
    if (!workspace) return null;
    const list = computeOnboardingChecklist({
      brandKeywords: Array.isArray(workspace.brandSetting?.brandKeyword) ? workspace.brandSetting.brandKeyword : [],
      campaigns: [{ keywords: workspace.keywords, accounts: workspace.accounts }],
    });
    return toOnboardingBadge(list, workspace.onboarding?.status);
  }, [workspace]);
}
